'use client';

import React from "react";
import Image from 'next/image';
import * as Accordion from "@radix-ui/react-accordion";

// Assets
import XIcon from '@public/png/x-1.png';
import CarretUpActive from "@public/png/caret-up-1.png"
import ArrowLeft from "@public/png/arrow-left-2.png"

// Components
import Modal from '@src/components/atoms/modal'
import Button from '@src/components/atoms/button'
import QuantityInput from '@src/components/atoms/form/input-quantity'

const formatNumber = (value) => {
  if (!value) return '';
  return parseInt(value, 10).toLocaleString('id-ID');
};

const PropertyOption = ({ label, isActive, ...props }) => {
  return (
    <button {...props} className={`${isActive ? "bg-white border border-solid border-[#F6F6F6]" : "bg-[#F6F6F6]"} h-[40px] flex items-center px-2 py-[5px] rounded w-fit`}>
      <span className={`text-[14px] ${isActive ? "font-satoshi-bold" : ""}`}>{label}</span>
      {isActive && <div className='ml-2 p-2 flex justify-center items-center bg-[#FFEAE9] rounded-2xl'>
        <Image
          width={12}
          height={12}
          src={XIcon}
          alt='Close'
          className='w-[12px] aspect-square object-contain'
        />
      </div>}
    </button>
  )
}

const Sort = ({
  params,
  setParams,
}) => {
  const handleSelect = (status, isActive) => {
    const newParams = { ...params }
    newParams["_order"] = isActive ? null : status
    setParams(newParams)
  }

  return (
    <div className="mb-4 flex flex-col top-0 left-0 w-full gap-4">
      <PropertyOption
        label="Harga Tinggi ke Rendah"
        onClick={() => handleSelect("highest-price", params["_order"] === "highest-price")}
        isActive={params["_order"] === "highest-price"}
      />
      <PropertyOption
        label="Harga Rendah ke Tinggi"
        onClick={() => handleSelect("lowest-price", params["_order"] === "lowest-price")}
        isActive={params["_order"] === "lowest-price"}
      />
    </div>
  )
}

const TipeProperti = ({
  data,
  params,
  setParams,
}) => {
  const handleSelect = (item, isActive) => {
    const newParams = { ...params }
    newParams.property_category_id = isActive ? null : item.id
    setParams(newParams)
  }
  return (
    <div className="mb-4 flex flex-wrap top-0 left-0 w-full gap-4">
      {!!data?.length && data.map((item, idx) => {
        const isActive = +params.property_category_id === item.id;

        return (
          <PropertyOption
            key={`list-category-${item.name}-${idx}`}
            label={item.name}
            onClick={() => handleSelect(item, isActive)}
            isActive={isActive}
          />
        )
      })}
    </div>
  )
}

const Price = ({ params, setParams }) => {
  const handleChange = (e) => {
    const value = e.target.value.replace(/\D/g, "")
    const name = e.target.name

    let newParams = { ...params };

    newParams[name] = value;
    setParams(newParams);
  };

  return (
    <div className='mb-4 pr-[32px]'>
      <div className='bg-white h-[40px] flex items-center border border-solid border-[#E6E7E8] rounded-sm p-[12px] mb-2'>
        <span className='flex items-center justify-center h-full text-[17px] pr-[12px] font-satoshi-bold'>
          Rp
        </span>
        <input
          className='h-[24px] text-[17px] border font-satoshi w-full border-[#E6E7E8] 
            placeholder:text-[#808285]'
          type="text"
          placeholder="Harga minimal"
          name="price[min]"
          value={formatNumber(params["price[min]"] ?? "")}
          onChange={handleChange}
        />
      </div>
      <div className='bg-white h-[40px] flex items-center border border-solid border-[#E6E7E8] rounded-sm p-[12px]'>
        <span className='flex items-center justify-center h-full text-[17px] pr-[12px] font-satoshi-bold'>
          Rp
        </span>
        <input
          name="price[max]"
          className='h-[24px] text-[17px] border font-satoshi w-full border-[#E6E7E8] 
            placeholder:text-[#808285]'
          type="text"
          placeholder="Harga maksimal"
          value={formatNumber(params["price[max]"] ?? "")}
          onChange={handleChange}
        />
      </div>
    </div>
  );
};

const LuasTanah = ({ params, setParams }) => {
  const handleChange = (e) => {
    const value = e.target.value.replace(/\D/g, "")
    const name = e.target.name

    let newParams = { ...params };

    newParams[name] = value;
    setParams(newParams);
  };

  return (
    <div className='flex items-center mb-4'>
      <div className='bg-white h-[40px] flex items-center border border-solid border-[#E6E7E8] rounded-[8px]'>
        <input
          name="surface_area[min]"
          className={`h-full p-[12px]  text-[17px] font-satoshi border rounded-[8px] w-full`}
          type="text"
          placeholder="min."
          value={params["surface_area[min]"] ?? ""}
          onChange={handleChange}
        />
        <span className='font-satoshi-bold flex items-center justify-center h-full text-[17px] p-[12px]'>
          m<sup>2</sup>
        </span>
      </div>
      <span className='text-[17px] font-sfpro-semibold mx-2'>-</span>
      <div className='bg-white h-[40px] flex items-center border border-solid border-[#E6E7E8] rounded-[8px]'>
        <input
          name="surface_area[max]"
          className={`h-full p-[12px] text-[17px] font-satoshi border rounded-[8px] w-full`}
          type="text"
          placeholder="max."
          value={params["surface_area[max]"] ?? ""}
          onChange={handleChange}
        />
        <span className='font-satoshi-bold p-[12px] flex items-center justify-center h-full text-[17px]'>
          m<sup>2</sup>
        </span>
      </div>
    </div>
  )
}

const LuasBangunan = ({ params, setParams }) => {
  const handleChange = (e) => {
    const value = e.target.value.replace(/\D/g, "")
    const name = e.target.name

    let newParams = { ...params };

    newParams[name] = value;
    setParams(newParams);
  };

  return (
    <div className='flex items-center mb-4'>
      <div className='bg-white h-[40px] flex items-center border border-solid border-[#E6E7E8] rounded-[8px]'>
        <input
          name="building_area[min]"
          className={`h-full p-[12px]  text-[17px] font-satoshi border rounded-[8px] w-full`}
          type="text"
          placeholder="min."
          value={params["building_area[min]"] ?? ""}
          onChange={handleChange}
        />
        <span className='font-satoshi-bold flex items-center justify-center h-full text-[17px] p-[12px]'>
          m<sup>2</sup>
        </span>
      </div>
      <span className='text-[17px] font-sfpro-semibold mx-2'>-</span>
      <div className='bg-white h-[40px] flex items-center border border-solid border-[#E6E7E8] rounded-[8px]'>
        <input
          name="building_area[max]"
          className={`h-full p-[12px] text-[17px] font-satoshi border rounded-[8px] w-full`}
          type="text"
          placeholder="max."
          value={params["building_area[max]"] ?? ""}
          onChange={handleChange}
        />
        <span className='font-satoshi-bold p-[12px] flex items-center justify-center h-full text-[17px]'>
          m<sup>2</sup>
        </span>
      </div>
    </div>
  )
}

const Ruangan = ({ params, setParams }) => {
  const handleChange = (value, name) => {
    let newParams = { ...params };
    newParams[name] = !!+value ? value : "";
    setParams(newParams);
  };
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-4">
        <p className="text-[14px] font-satoshi">Kamar Tidur</p>
        <QuantityInput
          onChange={handleChange}
          name="bedroom"
          value={params["bedroom"] ?? ""}
        />
      </div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[14px] font-satoshi text-[#808285] mb-1">Kamar Tidur</p>
          <p className="text-[11px] font- italic">*Asisten Rumah Tangga</p>
        </div>
        <QuantityInput
          onChange={handleChange}
          name={"additional_bedroom"}
          value={params["additional_bedroom"] ?? ""}
          disabled={!params["bedroom"]}
        />
      </div>

      <div className="flex items-center justify-between mb-4">
        <p className="text-[14px] font-satoshi">Kamar Mandi</p>
        <QuantityInput
          onChange={handleChange}
          name={"bathroom"}
          value={params["bathroom"] ?? ""}
        />
      </div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[14px] font-satoshi text-[#808285] mb-1">Kamar Mandi</p>
          <p className="text-[11px] font-satoshi italic">*Asisten Rumah Tangga</p>
        </div>
        <QuantityInput
          onChange={handleChange}
          name={"additional_bathroom"}
          value={params["additional_bathroom"] ?? ""}
          disabled={!params["bathroom"]}
        />
      </div>
    </div>
  )
}

const ModalFilterListListing = ({
  onClose,
  listCategory,
  applyFilter,
  openItem,
  setOpenItem,
  selectedCategory,
  handleResetForm,
  params,
  setParams,
  disabledHapusFilter,
  DisplayPrice,
  DisplayLT,
  DisplayLB,
  DisplayRuangan,
  selectedSort,
  ...props
}) => {
  return (
    <Modal {...props} onClose={onClose} className="!items-start dd2-xl:hidden">
      <div className="relative mt-[56px] z-[42] w-[460px] rounded-[12px] bg-white p-4">
        <button className="absolute z-[43] p-1 -top-8 right-0 flex justify-center items-center bg-white rounded">
          <Image
            width={16}
            height={16}
            src={XIcon}
            alt="Close"
            className="object-contain"
            onClick={onClose}
          />
        </button>

        <p className="text-[21px] font-satoshi-bold mb-2">Filter</p>

        <Accordion.Root
          type="single"
          collapsible
          value={openItem}
          onValueChange={(value) => setOpenItem(value)}
        >

          {/* Sort Accordion */}
          <Accordion.Item value="sort" className="border-b">
            <Accordion.Header>
              <Accordion.Trigger className="group py-4 w-full">
                <div className="flex w-full items-center justify-between">
                  <span className="text-[14px] font-satoshi">Urut berdasarkan</span>
                  <Image
                    width={16}
                    height={16}
                    src={CarretUpActive}
                    alt="Toggle"
                    className="object-contain transition-transform group-data-[state=open]:rotate-0 group-data-[state=closed]:-rotate-180"
                  />
                </div>
                {!!selectedSort && <p className="mt-1 text-start text-[14px] font-satoshi-bold">{selectedSort}</p>}
              </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Content className="pb-4">
              {/* Pass isOpen prop by comparing openItem value */}
              <Sort
                params={params}
                setParams={setParams}
                setOpenItem={setOpenItem}
              />
            </Accordion.Content>
          </Accordion.Item>

          {/* Tipe Properti Accordion */}
          <Accordion.Item value="tipe-properti" className="border-b">
            <Accordion.Header>
              <Accordion.Trigger className="group py-4 w-full">
                <div className="flex w-full items-center justify-between">
                  <span className="text-[14px] font-satoshi">Tipe Properti</span>
                  <Image
                    width={16}
                    height={16}
                    src={CarretUpActive}
                    alt="Toggle"
                    className="object-contain transition-transform group-data-[state=open]:rotate-0 group-data-[state=closed]:-rotate-180"
                  />
                </div>
                {!!selectedCategory && <p className="mt-1 text-start text-[14px] font-satoshi-bold">{selectedCategory}</p>}
              </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Content className="pb-4">
              {/* Pass isOpen prop by comparing openItem value */}
              <TipeProperti
                data={listCategory}
                params={params}
                setParams={setParams}
                setOpenItem={setOpenItem}
              />
            </Accordion.Content>
          </Accordion.Item>

          {/* Price Accordion */}
          <Accordion.Item value="price" className="border-b">
            <Accordion.Header>
              <Accordion.Trigger className="group py-4 w-full">
                <div className="flex w-full items-center justify-between">
                  <span className="text-[14px] font-satoshi">Harga</span>
                  <Image
                    width={16}
                    height={16}
                    src={CarretUpActive}
                    alt="Toggle"
                    className="object-contain transition-transform group-data-[state=open]:rotate-0 group-data-[state=closed]:-rotate-180"
                  />
                </div>
                {DisplayPrice}
              </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Content className="pb-4">
              <Price
                params={params}
                setParams={setParams}
                setOpenItem={setOpenItem}
              />
            </Accordion.Content>
          </Accordion.Item>

          {/* Luas Tanah Accordion */}
          <Accordion.Item value="luas-tanah" className="border-b">
            <Accordion.Header>
              <Accordion.Trigger className="group py-4 w-full">
                <div className="flex w-full items-center justify-between">
                  <span className="text-[14px] font-satoshi">Luas Tanah</span>
                  <Image
                    width={16}
                    height={16}
                    src={CarretUpActive}
                    alt="Toggle"
                    className="object-contain transition-transform group-data-[state=open]:rotate-0 group-data-[state=closed]:-rotate-180"
                  />
                </div>
                {DisplayLT}
              </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Content className="pb-4">
              <LuasTanah
                params={params}
                setParams={setParams}
                setOpenItem={setOpenItem}
              />
            </Accordion.Content>
          </Accordion.Item>

          {/* Luas Bangunan Accordion */}
          <Accordion.Item value="luas-bangunan" className="border-b">
            <Accordion.Header>
              <Accordion.Trigger className="group py-4 w-full">
                <div className="flex w-full items-center justify-between">
                  <span className="text-[14px] font-satoshi">Luas Bangunan</span>
                  <Image
                    width={16}
                    height={16}
                    src={CarretUpActive}
                    alt="Toggle"
                    className="object-contain transition-transform group-data-[state=open]:rotate-0 group-data-[state=closed]:-rotate-180"
                  />
                </div>
                {DisplayLB}
              </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Content className="pb-4">
              <LuasBangunan
                params={params}
                setParams={setParams}
                setOpenItem={setOpenItem}
              />
            </Accordion.Content>
          </Accordion.Item>

          {/* Ruangan Accordion */}
          <Accordion.Item value="ruangan" className="border-b">
            <Accordion.Header>
              <Accordion.Trigger className="group py-4 w-full">
                <div className="flex w-full items-center justify-between">
                  <span className="text-[14px] font-satoshi">Ruangan</span>
                  <Image
                    width={16}
                    height={16}
                    src={CarretUpActive}
                    alt="Toggle"
                    className="object-contain transition-transform group-data-[state=open]:rotate-0 group-data-[state=closed]:-rotate-180"
                  />
                </div>
                {DisplayRuangan}
              </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Content className="pb-4">
              <Ruangan
                params={params}
                setParams={setParams}
                setOpenItem={setOpenItem}
              />
            </Accordion.Content>
          </Accordion.Item>
        </Accordion.Root>

        <div className="flex items-center justify-end mt-4">
          <button disabled={disabledHapusFilter} onClick={handleResetForm} type="button" className="text-[14px] mr-4 font-satoshi underline disabled:text-[#808285]">
            Hapus Filter
          </button>
          <Button className="rounded-2" onClick={applyFilter} type="submit">Terapkan</Button>
        </div>
      </div>
    </Modal>
  );
};
const ModalFilterListListingMobile = ({
  onClose,
  listCategory,
  applyFilter,
  openItem,
  setOpenItem,
  selectedCategory,
  handleResetForm,
  params,
  setParams,
  disabledHapusFilter,
  DisplayPrice,
  DisplayLT,
  DisplayLB,
  DisplayRuangan,
  selectedSort,
  ...props
}) => {
  return (
    <Modal {...props} onClose={onClose} className="!items-start hidden dd-xl:flex">
      <div className="z-[42] flex flex-col justify-between w-screen h-screen bg-white py-4 overflow-y-auto">
        <div className="bg-[#FBFBFB] h-[calc(100vh-76px-32px)]">
          <div className='px-4 h-[40px] rounded-[8px] flex items-center justify-between'>
            <button className='w-[40px] h-[40px] mr-4 flex justify-center items-center bg-[#F6F6F6] rounded'>
              <Image
                width={20}
                height={20}
                src={ArrowLeft}
                alt='Close'
                className='object-contain'
                onClick={onClose}
              />
            </button>
            <p className="text-[17px] font-satoshi">Filter</p>
          </div>

          <div className="div-1 px-4 mt-4" />

          <div className="px-4">
            <Accordion.Root
              type="single"
              collapsible
              value={openItem}
              onValueChange={(value) => setOpenItem(value)}
            >

              {/* Sort Accordion */}
              <Accordion.Item value="sort" className="border-b">
                <Accordion.Header>
                  <Accordion.Trigger className="group py-4 w-full">
                    <div className="flex w-full items-center justify-between">
                      <span className="text-[14px] font-satoshi">Urut berdasarkan</span>
                      <Image
                        width={16}
                        height={16}
                        src={CarretUpActive}
                        alt="Toggle"
                        className="object-contain transition-transform group-data-[state=open]:rotate-0 group-data-[state=closed]:-rotate-180"
                      />
                    </div>
                    {!!selectedSort && <p className="mt-1 text-start text-[14px] font-satoshi-bold">{selectedSort}</p>}
                  </Accordion.Trigger>
                </Accordion.Header>
                <Accordion.Content className="pb-4">
                  {/* Pass isOpen prop by comparing openItem value */}
                  <Sort
                    params={params}
                    setParams={setParams}
                    setOpenItem={setOpenItem}
                  />
                </Accordion.Content>
              </Accordion.Item>

              {/* Tipe Properti Accordion */}
              <Accordion.Item value="tipe-properti" className="border-b">
                <Accordion.Header>
                  <Accordion.Trigger className="group py-4 w-full">
                    <div className="flex w-full items-center justify-between">
                      <span className="text-[14px] font-satoshi">Tipe Properti</span>
                      <Image
                        width={16}
                        height={16}
                        src={CarretUpActive}
                        alt="Toggle"
                        className="object-contain transition-transform group-data-[state=open]:rotate-0 group-data-[state=closed]:-rotate-180"
                      />
                    </div>
                    {!!selectedCategory && <p className="mt-1 text-start text-[14px] font-satoshi-bold">{selectedCategory}</p>}
                  </Accordion.Trigger>
                </Accordion.Header>
                <Accordion.Content className="pb-4">
                  {/* Pass isOpen prop by comparing openItem value */}
                  <TipeProperti
                    data={listCategory}
                    params={params}
                    setParams={setParams}
                    setOpenItem={setOpenItem}
                  />
                </Accordion.Content>
              </Accordion.Item>

              {/* Price Accordion */}
              <Accordion.Item value="price" className="border-b">
                <Accordion.Header>
                  <Accordion.Trigger className="group py-4 w-full">
                    <div className="flex w-full items-center justify-between">
                      <span className="text-[14px] font-satoshi">Harga</span>
                      <Image
                        width={16}
                        height={16}
                        src={CarretUpActive}
                        alt="Toggle"
                        className="object-contain transition-transform group-data-[state=open]:rotate-0 group-data-[state=closed]:-rotate-180"
                      />
                    </div>
                    {DisplayPrice}
                  </Accordion.Trigger>
                </Accordion.Header>
                <Accordion.Content className="pb-4">
                  <Price
                    params={params}
                    setParams={setParams}
                    setOpenItem={setOpenItem}
                  />
                </Accordion.Content>
              </Accordion.Item>

              {/* Luas Tanah Accordion */}
              <Accordion.Item value="luas-tanah" className="border-b">
                <Accordion.Header>
                  <Accordion.Trigger className="group py-4 w-full">
                    <div className="flex w-full items-center justify-between">
                      <span className="text-[14px] font-satoshi">Luas Tanah</span>
                      <Image
                        width={16}
                        height={16}
                        src={CarretUpActive}
                        alt="Toggle"
                        className="object-contain transition-transform group-data-[state=open]:rotate-0 group-data-[state=closed]:-rotate-180"
                      />
                    </div>
                    {DisplayLT}
                  </Accordion.Trigger>
                </Accordion.Header>
                <Accordion.Content className="pb-4">
                  <LuasTanah
                    params={params}
                    setParams={setParams}
                    setOpenItem={setOpenItem}
                  />
                </Accordion.Content>
              </Accordion.Item>

              {/* Luas Bangunan Accordion */}
              <Accordion.Item value="luas-bangunan" className="border-b">
                <Accordion.Header>
                  <Accordion.Trigger className="group py-4 w-full">
                    <div className="flex w-full items-center justify-between">
                      <span className="text-[14px] font-satoshi">Luas Bangunan</span>
                      <Image
                        width={16}
                        height={16}
                        src={CarretUpActive}
                        alt="Toggle"
                        className="object-contain transition-transform group-data-[state=open]:rotate-0 group-data-[state=closed]:-rotate-180"
                      />
                    </div>
                    {DisplayLB}
                  </Accordion.Trigger>
                </Accordion.Header>
                <Accordion.Content className="pb-4">
                  <LuasBangunan
                    params={params}
                    setParams={setParams}
                    setOpenItem={setOpenItem}
                  />
                </Accordion.Content>
              </Accordion.Item>

              {/* Ruangan Accordion */}
              <Accordion.Item value="ruangan" className="border-b">
                <Accordion.Header>
                  <Accordion.Trigger className="group py-4 w-full">
                    <div className="flex w-full items-center justify-between">
                      <span className="text-[14px] font-satoshi">Ruangan</span>
                      <Image
                        width={16}
                        height={16}
                        src={CarretUpActive}
                        alt="Toggle"
                        className="object-contain transition-transform group-data-[state=open]:rotate-0 group-data-[state=closed]:-rotate-180"
                      />
                    </div>
                    {DisplayRuangan}
                  </Accordion.Trigger>
                </Accordion.Header>
                <Accordion.Content className="pb-4">
                  <Ruangan
                    params={params}
                    setParams={setParams}
                    setOpenItem={setOpenItem}
                  />
                </Accordion.Content>
              </Accordion.Item>
            </Accordion.Root>
          </div>
        </div>

        <div className="fixed left-0 bottom-8 z-[43] w-screen flex bg-white px-4 items-center justify-end py-4 border-t-[0.5px] border-solid border-[#E6E7E8]">
          <button disabled={disabledHapusFilter} onClick={handleResetForm} type="button" className="text-[14px] mr-4 font-satoshi underline disabled:text-[#808285]">
            Hapus Filter
          </button>
          <Button className="rounded-2" onClick={applyFilter} type="submit">Terapkan</Button>
        </div>
      </div>
    </Modal>
  );
};

const Wrapper = ({ isOpen, ...props }) => {
  if (!isOpen) return null
  return <>
    <ModalFilterListListing isOpen={isOpen} {...props} />
    <ModalFilterListListingMobile isOpen={isOpen} {...props} />
  </>
}

export default Wrapper